import * as React from 'react';
import Pagination from '../Pagination';
import { Pencil, Trash2 } from 'lucide-react';
import { CustomAlertDialog } from '../AlertDialog';
import { RootState } from '@/types';
import { useSelector } from 'react-redux';

import { useDeleteBenefitMutation } from '@/store/benefitApi';
import { toast } from 'sonner';

interface Ledger {
  ledger: string;
  _id: string;
}

interface Benefit {
  ledger: Ledger;
  remark?: string;
  amount: number;
  date: string;
  month: string;
  year: string;
  createdAt: string;
  updatedAt: string;
  _id: string;
  __v: number;
}

export default function RecentBenefits({
  filterBySearchTerm,
  setIsOpen,
  setEditData,
}) {
  const [deleteBenefit, { isSuccess: deleteSuccess }] = useDeleteBenefitMutation();
  const { user } = useSelector((state: RootState) => state.user);

  const handleDelete = (id) => {
    deleteBenefit({
      benefitId: id,
      Token: user?.token,
    });
  };

  const handleEdit = (item) => {
    setIsOpen(true);
    setEditData(item);
  };

  React.useEffect(() => {
    if (deleteSuccess) {
      toast.success('Benefit transaction deleted successfully');
    }
  }, [deleteSuccess]);

  return (
    <div className="overflow-x-auto">
      <Pagination data={filterBySearchTerm} itemsPerPage={10}>
        {(paginatedData) => (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-600">
                  Date
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600">
                  Category
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600">
                  Description
                </th>
                <th className="text-right py-3 px-4 font-semibold text-gray-600">
                  Amount
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData?.map((item: Benefit, index) => (
                <tr
                  key={index}
                  className="border-b border-muted hover:bg-muted"
                >
                  <td className="py-3 px-4 text-foreground">
                    {new Date(item.date).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </td>
                  <td className="py-3 px-4 text-foreground">{item?.ledger?.ledger}</td>
                  <td className="py-3 px-4 text-foreground">{item?.remark}</td>
                  <td className="py-3 px-4 text-right text-emerald-600 font-semibold">₹{item?.amount}</td>
                  <td className="pl-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1 rounded-full hover:bg-blue-50 text-blue-600 transition-all duration-300 transform hover:scale-110"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <CustomAlertDialog
                        title="Delete Benefit Inflow"
                        description={
                          <>
                            Are you sure you want to delete this benefit entry
                            <span className="font-semibold text-emerald-600">
                              {' '}
                              "{item?.ledger?.ledger}"{' '}
                            </span>
                            with description
                            <span className="font-semibold text-emerald-600">
                              {' '}
                              "{item?.remark}"{' '}
                            </span>
                            amounting to
                            <span className="font-semibold text-emerald-600">
                              {' '}
                              ₹{item?.amount}{' '}
                            </span>
                            ?
                            <br />
                            <br /> *The amount will be deducted from the total benefits.
                          </>
                        }
                        onConfirm={() => handleDelete(item?._id)}
                        triggerButton={
                          <button className="p-1 rounded-full hover:bg-red-50 text-red-600 transition-all duration-300 transform hover:scale-110">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        }
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Pagination>
    </div>
  );
}
